import { Sparkles, ArrowRight, ArrowLeft, User, Wand2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { ProfilePhotoUpload } from "@/components/flowly/ProfilePhotoUpload/ProfilePhotoUpload";
import {
  PROFILE_PHOTO_EXAMPLE_BEFORE,
  PROFILE_PHOTO_EXAMPLE_AFTER,
} from "@/config/profilePhotoPresets";
import { PROFILE_PHOTO_CONFIG_ANCHOR_ID } from "@/pages/ProfilePhoto/constants";
import type { UploadedSource } from "@/pages/ProfilePhoto/types";

interface ProfilePhotoHeroProps {
  uploads: UploadedSource[];
  results: string[];
  /** Last finished generation (source + result), shown after the upload form
   *  resets so the before/after keeps the user's images instead of defaults. */
  lastShowcase: { before: string; after: string } | null;
  isUploading: boolean;
  canGenerate: boolean;
  onAddFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  onGenerate: () => void;
}

export function ProfilePhotoHero({
  uploads,
  results,
  lastShowcase,
  isUploading,
  canGenerate,
  onAddFiles,
  onRemove,
  onGenerate,
}: ProfilePhotoHeroProps) {
  const { t, isRTL } = useLanguage();

  // Hero "before/after" pair:
  //  - "before" → user's first upload, else the configured example image
  //  - "after"  → user's first generated result; once the user has uploaded
  //               (but not yet generated), drop the example and show an empty
  //               "Your Photo" placeholder (with an orange CTA) to build
  //               anticipation for their result
  const hasUpload = uploads.length > 0;
  const beforeImage =
    uploads[0]?.previewUrl || lastShowcase?.before || PROFILE_PHOTO_EXAMPLE_BEFORE || undefined;
  const afterImage =
    results[0] ||
    (hasUpload ? undefined : lastShowcase?.after || PROFILE_PHOTO_EXAMPLE_AFTER || undefined);
  const afterShowsPlaceholder = !results[0] && hasUpload;

  return (
    <div className="rounded-3xl border border-border bg-gradient-to-b from-primary/[0.05] to-transparent p-5 md:p-8">
      <div className="text-center mb-6 md:mb-8">
        <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-2 md:mb-3 bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
          {t.profilePhoto.heroTitle}
        </h1>
        <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
          {t.profilePhoto.heroSubtitle}
        </p>
      </div>

      {hasUpload ? (
        // After first upload: stack the showcase prominently on top, with a
        // smaller upload-management strip below for adding/removing photos.
        <div className="space-y-4 md:space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="max-w-md mx-auto w-full">
            <BeforeAfterShowcase
              beforeImage={beforeImage}
              afterImage={afterImage}
              afterPlaceholder={
                afterShowsPlaceholder
                  ? {
                      caption: t.profilePhoto.afterPlaceholder,
                      ctaLabel: t.profilePhoto.createNowCta,
                      canGenerate,
                      onGenerate,
                    }
                  : undefined
              }
              isRTL={isRTL}
            />
          </div>
          <div className="max-w-sm mx-auto w-full">
            <ProfilePhotoUpload
              uploads={uploads}
              isUploading={isUploading}
              onAddFiles={onAddFiles}
              onRemove={onRemove}
              variant="compact"
            />
          </div>
        </div>
      ) : (
        // Initial state: showcase + big drop-zone side-by-side.
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 items-start">
          <BeforeAfterShowcase
            beforeImage={beforeImage}
            afterImage={afterImage}
            afterPlaceholder={undefined}
            isRTL={isRTL}
          />

          <ProfilePhotoUpload
            uploads={uploads}
            isUploading={isUploading}
            onAddFiles={onAddFiles}
            onRemove={onRemove}
            variant="hero"
          />
        </div>
      )}
    </div>
  );
}

interface AfterPlaceholder {
  caption: string;
  ctaLabel: string;
  canGenerate: boolean;
  onGenerate: () => void;
}

function BeforeAfterShowcase({
  beforeImage,
  afterImage,
  afterPlaceholder,
  isRTL,
}: {
  beforeImage: string | undefined;
  afterImage: string | undefined;
  afterPlaceholder: AfterPlaceholder | undefined;
  isRTL: boolean;
}) {
  const { t } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div className="grid grid-cols-2 gap-3 md:gap-4 relative">
      <ShowcaseCard
        label={t.profilePhoto.beforeLabel}
        kind="before"
        image={beforeImage}
      />
      <ShowcaseCard
        label={t.profilePhoto.afterLabel}
        kind="after"
        image={afterImage}
        placeholder={afterPlaceholder}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background border border-border shadow-md flex items-center justify-center pointer-events-none">
        <Arrow className="w-4 h-4 text-primary" />
      </div>
    </div>
  );
}

function ShowcaseCard({
  label,
  kind,
  image,
  placeholder,
}: {
  label: string;
  kind: "before" | "after";
  image?: string;
  placeholder?: AfterPlaceholder;
}) {
  const Icon = kind === "before" ? User : Wand2;
  return (
    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-muted to-secondary">
      {image ? (
        <img
          src={image}
          alt={label}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : placeholder ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-3 text-center">
          <button
            type="button"
            onClick={() => {
              if (placeholder.canGenerate) {
                placeholder.onGenerate();
                return;
              }
              const target = document.getElementById(PROFILE_PHOTO_CONFIG_ANCHOR_ID);
              target?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className={cn(
              "inline-flex items-center justify-center gap-2 px-4 md:px-5 h-11 md:h-12 rounded-xl bg-gradient-to-r gradient-primary text-white text-sm md:text-base font-bold whitespace-nowrap transition-all",
              "shadow-[0_0_20px_hsl(var(--brand)/0.35),0_4px_16px_rgba(0,0,0,0.2)] hover:shadow-[0_0_28px_hsl(var(--brand)/0.55)] hover:scale-[1.04] active:scale-[0.97]",
            )}
          >
            <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
            <span>{placeholder.ctaLabel}</span>
          </button>
          <span className="text-sm md:text-base font-semibold text-foreground">
            {placeholder.caption}
          </span>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="w-10 h-10 text-primary/30" />
        </div>
      )}
      <div className="absolute top-2 md:top-2.5 start-2 md:start-2.5">
        <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-md bg-black/75 backdrop-blur-sm text-[10px] md:text-xs font-bold text-white uppercase tracking-wider shadow-sm ring-1 ring-white/10">
          {label}
        </span>
      </div>
    </div>
  );
}
